import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Upload, FileText, Loader2, CheckCircle, Languages, Check, X,
  Image, BookOpen, ArrowRight, AlertCircle, ScanLine, Combine, LayoutTemplate, FileCheck,
  LogOut, Plus, Library, ShieldCheck, Clock
} from 'lucide-react';

// 流程模式：纯校对 / 校对+翻译
type Track = 'proofread' | 'translate';
type StageStatus = 'pending' | 'running' | 'done' | 'error';
type JobState = 'idle' | 'analyzing' | 'review' | 'applying' | 'done' | 'error';

interface Stage {
  key: string;
  name: string;
  desc: string;
  icon: React.ComponentType<{ className?: string }>;
  optional?: boolean;
  clickable?: boolean;
}

interface Suggestion {
  id: string;
  type: string | null;
  para_index: number;
  original: string;
  suggestion: string;
  reason: string;
  decision: 'pending' | 'accepted' | 'ignored';
  text: string; // 可微调的采纳文本，默认= suggestion
}
interface ReviewChapter {
  n: number;
  title: string | null;
  paragraphs?: number;
  suggestions: Suggestion[];
}

// 书架条目（GET /api/studio/jobs）
interface ShelfJob {
  id: string;
  title: string | null;
  source_filename: string;
  track: Track;
  status: string;
  chapters_total: number | null;
  created_at: string;
  updated_at: string;
}

const statusLabel: Record<string, { text: string; cls: string }> = {
  created: { text: '新建', cls: 'bg-gray-100 text-gray-600' },
  awaiting_review: { text: '待审阅建议', cls: 'bg-amber-100 text-amber-700' },
  applying: { text: '处理中', cls: 'bg-blue-100 text-blue-700' },
  done: { text: '已完成', cls: 'bg-green-100 text-green-700' },
  error: { text: '出错', cls: 'bg-red-100 text-red-700' },
};

// 环节定义：模式 → 上传 → 拆章 → 编辑反馈 → 文字校对 →(翻译·译校)→ 合并 → 版式 → 终校 → 书号&版权页 → 出稿PDF → 封面
function buildStages(track: Track): Stage[] {
  const head: Stage[] = [
    { key: 'mode', name: '选择模式', desc: '是否需要翻译', icon: Languages, clickable: true },
    { key: 'upload', name: '上传原稿', desc: '接收 docx 原稿', icon: Upload, clickable: true },
    { key: 'chapter', name: '拆章', desc: '按标题样式拆分章节', icon: ScanLine },
    { key: 'feedback', name: '编辑反馈', desc: 'AI 出修改建议，人工逐章采纳', icon: FileText, clickable: true },
    { key: 'proofread', name: '文字校对', desc: '错别字 / 标点 / 格式（自动）', icon: FileCheck },
  ];
  const translateSteps: Stage[] = [
    { key: 'translate', name: '逐章翻译', desc: 'AI 翻译（插件）', icon: Languages, optional: true },
    { key: 'translate-check', name: '译文校对', desc: '对照原文核对（插件）', icon: FileCheck, optional: true },
  ];
  const tail: Stage[] = [
    { key: 'combine', name: '合并', desc: '合并成整本', icon: Combine },
    { key: 'pagestyle', name: '版式排版', desc: 'KDP 判型 + 封二/封三', icon: LayoutTemplate },
    { key: 'finalcheck', name: '终校 / QA', desc: '成品页上再校（断行/页码/目录）', icon: FileCheck },
    { key: 'copyright', name: '书号 & 版权页', desc: '分配 ISBN → 版权页 + 条码', icon: BookOpen },
    { key: 'final', name: '出稿 PDF', desc: '内文 PDF，交付前定稿', icon: FileText },
    { key: 'cover', name: '封面设计', desc: '封一 + 书脊 + 封底（依据成品页数）', icon: Image, optional: true },
  ];
  return track === 'translate' ? [...head, ...translateSteps, ...tail] : [...head, ...tail];
}

// 服务端 apply 阶段完成的环节
const APPLY_KEYS = ['proofread', 'translate', 'translate-check', 'combine'];

function computeStatuses(stages: Stage[], file: File | null, js: JobState, hasJob = false): Record<string, StageStatus> {
  const map: Record<string, StageStatus> = {};
  const applied = js === 'done';
  const applying = js === 'applying';
  const started = js !== 'idle'; // 已提交分析
  for (const s of stages) {
    switch (s.key) {
      case 'mode':
        map[s.key] = 'done'; break;
      // 从书架读回的任务没有本地 File，但稿件早已上传过，不该显示成待办。
      case 'upload':
        map[s.key] = file || hasJob ? 'done' : 'running'; break;
      case 'chapter':
        map[s.key] = !started ? 'pending' : js === 'analyzing' ? 'running' : 'done';
        break;
      case 'feedback':
        map[s.key] = js === 'analyzing' ? 'pending'
          : js === 'review' ? 'running'
          : (applying || applied) ? 'done' : 'pending';
        break;
      default:
        if (APPLY_KEYS.includes(s.key)) {
          map[s.key] = applied ? 'done' : applying ? 'running' : 'pending';
        } else {
          map[s.key] = 'pending'; // 版式/终校/版权页/出稿PDF/封面：待接入
        }
    }
  }
  if (js === 'error') {
    for (const k of APPLY_KEYS) if (map[k] === 'running') map[k] = 'error';
  }
  return map;
}

const chipStyle: Record<StageStatus, string> = {
  pending: 'bg-gray-100 border-gray-200 text-gray-500',
  running: 'bg-blue-500 border-blue-500 text-white',
  done: 'bg-green-500 border-green-500 text-white',
  error: 'bg-red-500 border-red-500 text-white',
};

const typeColor: Record<string, string> = {
  标点: 'bg-amber-100 text-amber-700',
  格式: 'bg-sky-100 text-sky-700',
  错别字: 'bg-rose-100 text-rose-700',
  语法: 'bg-violet-100 text-violet-700',
  一致性: 'bg-teal-100 text-teal-700',
  润色: 'bg-indigo-100 text-indigo-700',
};

const Studio: React.FC = () => {
  const navigate = useNavigate();
  const [authorEmail, setAuthorEmail] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [view, setView] = useState<'shelf' | 'job'>('shelf');
  const [shelf, setShelf] = useState<ShelfJob[]>([]);
  const [shelfLoading, setShelfLoading] = useState(true);
  const [bookTitle, setBookTitle] = useState('');
  const [rightsAck, setRightsAck] = useState(false);
  const [track, setTrack] = useState<Track>('proofread');
  const [coverEnabled, setCoverEnabled] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [jobState, setJobState] = useState<JobState>('idle');
  const [jobId, setJobId] = useState<string | null>(null);
  const [chapters, setChapters] = useState<ReviewChapter[]>([]);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [activeTab, setActiveTab] = useState<string>('mode');
  const inputRef = useRef<HTMLInputElement>(null);

  // 未登录（或档案未填完）一律送去 /author，带 next 回来。
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/studio/auth/me', { credentials: 'same-origin' });
        if (cancelled) return;
        if (!res.ok) {
          navigate('/author?next=/studio', { replace: true });
          return;
        }
        const data = await res.json();
        if (!data?.success || !data.author?.profile_completed) {
          navigate('/author?next=/studio', { replace: true });
          return;
        }
        setAuthorEmail(data.author.email);
        setAuthChecked(true);
      } catch {
        if (!cancelled) {
          setErrorMsg('无法连接服务器，请稍后重试');
          setAuthChecked(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  // 书架：从 D1 读回本人所有任务。刷新页面不再丢失 awaiting_review 的任务。
  const loadShelf = useCallback(async () => {
    setShelfLoading(true);
    try {
      const res = await fetch('/api/studio/jobs', { credentials: 'same-origin' });
      if (res.status === 401) { navigate('/author?next=/studio', { replace: true }); return; }
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `载入失败 (${res.status})`);
      setShelf(data.jobs || []);
    } catch (err: any) {
      setErrorMsg(err.message || '书架载入失败');
    } finally {
      setShelfLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (authorEmail) loadShelf();
  }, [authorEmail, loadShelf]);

  const logout = async () => {
    await fetch('/api/studio/auth/logout', { method: 'POST', credentials: 'same-origin' });
    navigate('/author', { replace: true });
  };

  // 新建书稿：清空上一本的残留状态，回到第一环节。
  const startNewJob = () => {
    setFile(null);
    setJobId(null);
    setChapters([]);
    setDownloadUrl('');
    setBookTitle('');
    setRightsAck(false);
    setTrack('proofread');
    setCoverEnabled(false);
    setJobState('idle');
    setErrorMsg('');
    setActiveTab('mode');
    setView('job');
  };

  const stages = useMemo(() => buildStages(track), [track]);
  const statuses = useMemo(() => computeStatuses(stages, file, jobState, !!jobId), [stages, file, jobState, jobId]);
  const activeStage = stages.find(s => s.key === activeTab);

  const totalSug = useMemo(() => chapters.reduce((a, c) => a + c.suggestions.length, 0), [chapters]);
  const acceptedSug = useMemo(
    () => chapters.reduce((a, c) => a + c.suggestions.filter(s => s.decision === 'accepted').length, 0),
    [chapters],
  );

  const pickFile = (f: File | null) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.docx')) {
      setErrorMsg('请上传 .docx 文档');
      setFile(null);
      return;
    }
    setErrorMsg('');
    setFile(f);
    setJobState('idle');
  };

  const selectTrack = (t: Track) => { setTrack(t); setActiveTab('upload'); };

  // 阶段一：分析（拆章 + AI 编辑反馈）
  const handleAnalyze = async () => {
    if (!file) return;
    setJobState('analyzing');
    setErrorMsg('');
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('track', track);
      form.append('cover', String(coverEnabled));
      form.append('title', bookTitle.trim());
      form.append('rights_ack', String(rightsAck));
      const res = await fetch('/api/studio/jobs', { method: 'POST', credentials: 'same-origin', body: form });
      if (res.status === 401) { navigate('/author?next=/studio', { replace: true }); return; }
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `分析失败 (${res.status})`);
      setJobId(data.job_id);
      setChapters((data.chapters || []).map((c: any) => ({
        n: c.n,
        title: c.title,
        paragraphs: c.paragraphs,
        suggestions: (c.suggestions || []).map((s: any) => ({ ...s, text: s.suggestion })),
      })));
      setJobState('review');
      setActiveTab('feedback');
      loadShelf();
    } catch (err: any) {
      setJobState('error');
      setErrorMsg(err.message || '提交失败，请重试');
    }
  };

  const setDecision = (ci: number, si: number, decision: Suggestion['decision']) =>
    setChapters(prev => prev.map((c, i) => i !== ci ? c : {
      ...c, suggestions: c.suggestions.map((s, j) => j !== si ? s : { ...s, decision }),
    }));

  const setText = (ci: number, si: number, text: string) =>
    setChapters(prev => prev.map((c, i) => i !== ci ? c : {
      ...c, suggestions: c.suggestions.map((s, j) => j !== si ? s : { ...s, text }),
    }));

  const bulk = (decision: Suggestion['decision']) =>
    setChapters(prev => prev.map(c => ({ ...c, suggestions: c.suggestions.map(s => ({ ...s, decision })) })));

  // 阶段二：应用采纳的修改 → 校对 → 合并
  const handleApply = async () => {
    if (!jobId) return;
    setJobState('applying');
    setErrorMsg('');
    try {
      const items = chapters.flatMap(c => c.suggestions.map(s => ({
        id: s.id, n: c.n, para_index: s.para_index,
        decision: s.decision === 'accepted' ? 'accepted' : 'ignored',
        new_text: s.text,
      })));
      const res = await fetch(`/api/studio/jobs/${jobId}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items }),
      });
      if (res.status === 401) { navigate('/author?next=/studio', { replace: true }); return; }
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `处理失败 (${res.status})`);
      setDownloadUrl(data.download_url);
      setJobState('done');
      loadShelf();
    } catch (err: any) {
      setJobState('error');
      setErrorMsg(err.message || '处理失败，请重试');
    }
  };

  // 从服务器读回某任务的章节+建议（半自动：建议由 Claude 侧写入 D1 后，前端刷新查看）
  const loadJob = async (id: string) => {
    const jid = id.trim();
    if (!jid) return;
    setErrorMsg('');
    try {
      const res = await fetch(`/api/studio/jobs/${jid}/suggestions`, { credentials: 'same-origin' });
      if (res.status === 401) { navigate('/author?next=/studio', { replace: true }); return; }
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `载入失败 (${res.status})`);
      setJobId(data.job_id);
      setBookTitle(data.title || '');
      if (data.track) setTrack(data.track);
      setChapters((data.chapters || []).map((c: any) => ({
        n: c.n,
        title: c.title,
        paragraphs: c.paragraphs,
        suggestions: (c.suggestions || []).map((s: any) => ({
          ...s, text: s.edited_text ?? s.suggestion,
        })),
      })));
      if (data.status === 'done') { setDownloadUrl(data.download_url || ''); setJobState('done'); }
      else setJobState('review');
      setRightsAck(true); // 该稿上传时已经声明过，读回时不必再勾
      setActiveTab('feedback');
      setView('job');
    } catch (err: any) {
      setErrorMsg(err.message || '载入失败');
    }
  };

  const chipIcon = (st: StageStatus, Icon: Stage['icon']) =>
    st === 'done' ? <CheckCircle className="h-4 w-4" />
      : st === 'running' ? <Loader2 className="h-4 w-4 animate-spin" />
      : st === 'error' ? <AlertCircle className="h-4 w-4" />
      : <Icon className="h-4 w-4" />;

  // 登录检查未完成前不渲染正文，避免未登录用户瞥见工作台。
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>Studio — 书籍工作台 | Woodlogos</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
            <div className="flex items-center gap-2 text-blue-600">
              <BookOpen className="h-6 w-6" />
              <span className="text-sm font-semibold tracking-wide uppercase">Woodlogos Studio</span>
            </div>
            {authorEmail && (
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span className="truncate max-w-[200px]">{authorEmail}</span>
                <button onClick={logout} className="inline-flex items-center gap-1 hover:text-gray-900">
                  <LogOut className="h-3.5 w-3.5" /> 退出
                </button>
              </div>
            )}
          </div>

          {view === 'shelf' ? (
            <>
              <h1 className="text-3xl font-bold text-gray-900">我的书架</h1>
              <p className="text-gray-600 mt-2">你提交过的书稿都在这里，点开任意一本可继续未完成的环节。</p>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold text-gray-900">
                {bookTitle || '书籍自动校对 / 翻译工作台'}
              </h1>
              <p className="text-gray-600 mt-2">
                上传 → 拆章 → AI 编辑反馈（人工采纳）→ 文字校对 →（翻译）→ 合并 → 版式 → 出稿。人工只需分配 ISBN、上传 KDP。
              </p>
              <button
                onClick={() => { setView('shelf'); loadShelf(); }}
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                <Library className="h-4 w-4" /> 返回书架
              </button>
            </>
          )}
        </div>

        {/* 书架 */}
        {view === 'shelf' && (
          <section className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                书稿{shelf.length > 0 && ` · ${shelf.length} 本`}
              </h2>
              <button
                onClick={startNewJob}
                className="inline-flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition"
              >
                <Plus className="h-4 w-4" /> 新建书稿
              </button>
            </div>

            {shelfLoading ? (
              <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 text-gray-400 animate-spin" /></div>
            ) : shelf.length === 0 ? (
              <div className="py-10 text-center">
                <Library className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">还没有书稿。点「新建书稿」上传你的第一份原稿。</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {shelf.map(j => {
                  const st = statusLabel[j.status] || { text: j.status, cls: 'bg-gray-100 text-gray-600' };
                  return (
                    <li key={j.id}>
                      <button
                        onClick={() => loadJob(j.id)}
                        className="w-full text-left py-3 flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 -mx-2 transition"
                      >
                        <FileText className="h-5 w-5 text-blue-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">{j.title || j.source_filename}</div>
                          <div className="text-xs text-gray-400 flex items-center gap-2 mt-0.5">
                            <span>{j.track === 'translate' ? 'B 线 · 校对+翻译' : 'A 线 · 仅校对'}</span>
                            {j.chapters_total != null && j.chapters_total > 0 && <span>· {j.chapters_total} 节</span>}
                            <span className="inline-flex items-center gap-1">
                              <Clock className="h-3 w-3" /> {new Date(j.updated_at + 'Z').toLocaleString('zh-CN')}
                            </span>
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full shrink-0 ${st.cls}`}>{st.text}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {errorMsg && (
              <div className="flex items-center gap-2 text-red-600 text-sm mt-4"><AlertCircle className="h-4 w-4" /> {errorMsg}</div>
            )}
          </section>
        )}

        {/* 进度 Tabs：已完成=绿底，进行中=蓝底，未处理=灰底 */}
        {view === 'job' && (
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            {stages.map((s, i) => {
              const st = statuses[s.key];
              const isActive = activeTab === s.key;
              const cls = `flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs whitespace-nowrap transition hover:opacity-90 ${chipStyle[st]} ${isActive ? 'ring-2 ring-offset-1 ring-blue-300' : ''}`;
              return (
                <React.Fragment key={s.key}>
                  {i > 0 && <span className="text-gray-300 shrink-0">›</span>}
                  <button onClick={() => setActiveTab(s.key)} className={cls} title={s.desc}>
                    <span className="flex items-center justify-center h-5 w-5 shrink-0">{chipIcon(st, s.icon)}</span>
                    <span className="font-medium">{s.name}</span>
                    {s.optional && (
                      <span className={`text-[9px] px-1 py-0.5 rounded ${st === 'pending' ? 'bg-purple-100 text-purple-600' : 'bg-white/25 text-white'}`}>插件</span>
                    )}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>
        )}

        {view === 'job' && (
        <>
        {/* 面板：模式选择 */}
        {activeTab === 'mode' && (
          <section className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">选择模式</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <button onClick={() => selectTrack('proofread')} className={`text-left border rounded-lg p-4 transition ${track === 'proofread' ? 'border-blue-500 ring-2 ring-blue-100 bg-blue-50/40' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className="flex items-center gap-2 font-medium text-gray-900"><FileCheck className="h-5 w-5 text-blue-600" /> A 线 · 仅校对</div>
                <p className="text-sm text-gray-500 mt-1">原文直接出版，不翻译</p>
              </button>
              <button onClick={() => selectTrack('translate')} className={`text-left border rounded-lg p-4 transition ${track === 'translate' ? 'border-blue-500 ring-2 ring-blue-100 bg-blue-50/40' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className="flex items-center gap-2 font-medium text-gray-900"><Languages className="h-5 w-5 text-blue-600" /> B 线 · 校对 + 翻译</div>
                <p className="text-sm text-gray-500 mt-1">校对后逐章翻译并核对</p>
              </button>
            </div>
            <label className="flex items-center gap-3 mt-4 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
              <input type="checkbox" checked={coverEnabled} onChange={e => setCoverEnabled(e.target.checked)} className="h-4 w-4 text-blue-600 rounded" />
              <Image className="h-5 w-5 text-gray-500" />
              <span className="text-sm text-gray-700"><span className="font-medium">封面插件</span> — 出稿后依据成品页数生成封面（封一 + 书脊 + 封底）</span>
            </label>
            <button onClick={() => setActiveTab('upload')} className="mt-4 inline-flex items-center gap-2 text-blue-600 text-sm font-medium hover:text-blue-700">
              下一步：上传原稿 <ArrowRight className="h-4 w-4" />
            </button>
          </section>
        )}

        {/* 面板：上传 */}
        {activeTab === 'upload' && (
          <section className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
              上传原稿 · 当前模式：{track === 'translate' ? 'B 线（校对 + 翻译）' : 'A 线（仅校对）'}
            </h2>

            <div className="mb-4">
              <label htmlFor="bookTitle" className="block text-sm font-medium text-gray-700 mb-1">
                书名 <span className="text-red-500">*</span>
              </label>
              <input
                id="bookTitle"
                value={bookTitle}
                disabled={jobState !== 'idle'}
                onChange={e => setBookTitle(e.target.value)}
                placeholder="这本书的名字"
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:border-blue-400 focus:ring-1 focus:ring-blue-100 disabled:bg-gray-50"
              />
            </div>

            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0] || null); }}
              onClick={() => inputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}`}
            >
              <input ref={inputRef} type="file" accept=".docx" className="hidden" onChange={e => pickFile(e.target.files?.[0] || null)} />
              {file ? (
                <div className="flex items-center justify-center gap-2 text-gray-800">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <span className="font-medium">{file.name}</span>
                  <span className="text-sm text-gray-400">({(file.size / 1024).toFixed(0)} KB)</span>
                </div>
              ) : (
                <>
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">点击或拖拽 <span className="font-medium">.docx</span> 文档到此处</p>
                </>
              )}
            </div>

            {/* 权利声明：针对本次上传的这一份稿件，每稿一次，提交时落库留证。 */}
            <label className="flex items-start gap-3 mt-4 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={rightsAck}
                disabled={jobState !== 'idle'}
                onChange={e => setRightsAck(e.target.checked)}
                className="mt-0.5 h-4 w-4 text-blue-600 rounded shrink-0"
              />
              <span className="text-sm text-gray-700">
                <span className="inline-flex items-center gap-1 font-medium text-gray-900">
                  <ShieldCheck className="h-4 w-4 text-green-600" /> 著作权声明
                </span>
                <span className="block mt-1 text-gray-600 leading-relaxed">
                  我确认对本次上传的《{bookTitle.trim() || '……'}》拥有完整著作权，或已取得权利人的合法授权；
                  稿件不侵犯任何第三方的著作权、商标权、隐私权或其他权利。若因稿件内容引发纠纷，由我承担相应责任。
                </span>
              </span>
            </label>

            {errorMsg && jobState !== 'review' && (
              <div className="flex items-center gap-2 text-red-600 text-sm mt-3"><AlertCircle className="h-4 w-4" /> {errorMsg}</div>
            )}

            <button
              onClick={handleAnalyze}
              disabled={!file || !bookTitle.trim() || !rightsAck || jobState === 'analyzing'}
              className="mt-4 inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-lg font-medium disabled:opacity-40 hover:bg-blue-700 transition"
            >
              {jobState === 'analyzing' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              拆章 + AI 编辑反馈
            </button>
            {jobState === 'analyzing' && (
              <p className="text-xs text-gray-400 mt-3">引擎首次唤醒约需 1–2 分钟（Render 冷启动），拆章 + 逐章分析中，请勿关闭页面。</p>
            )}
            {jobState === 'review' && (
              <p className="text-xs text-green-600 mt-3 flex items-center gap-1"><CheckCircle className="h-4 w-4" /> 分析完成，请到「编辑反馈」逐章采纳修改。</p>
            )}

            <div className="mt-6 pt-4 border-t border-gray-100">
              <button
                onClick={() => { setView('shelf'); loadShelf(); }}
                className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
              >
                <Library className="h-4 w-4" /> 打开已有书稿（返回书架）
              </button>
            </div>
          </section>
        )}

        {/* 面板：编辑反馈（AI 建议 → 人工采纳） */}
        {activeTab === 'feedback' && jobId && (
          <section className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                编辑反馈 · AI 修改建议（共 {totalSug} 条 · 已采纳 {acceptedSug}）
              </h2>
              <div className="flex gap-2">
                {jobId && (
                  <button onClick={() => loadJob(jobId)} className="text-xs px-3 py-1.5 rounded-lg border border-blue-300 text-blue-700 hover:bg-blue-50">刷新建议</button>
                )}
                {totalSug > 0 && jobState === 'review' && (
                  <>
                    <button onClick={() => bulk('accepted')} className="text-xs px-3 py-1.5 rounded-lg border border-green-300 text-green-700 hover:bg-green-50">全部采纳</button>
                    <button onClick={() => bulk('ignored')} className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">全部忽略</button>
                  </>
                )}
              </div>
            </div>

            {totalSug === 0 && (
              <p className="text-sm text-gray-500 mb-4">AI 未发现明显需修改处，可直接合并出稿。</p>
            )}

            <div className="space-y-6">
              {chapters.filter(c => c.suggestions.length > 0).map((c) => {
                const realCi = chapters.indexOf(c);
                return (
                  <div key={c.n}>
                    <div className="text-sm font-semibold text-gray-800 mb-2">
                      {c.n === 0 ? '前置内容' : `第 ${c.n} 章`} · {c.title}
                      <span className="ml-2 text-xs text-gray-400">{c.suggestions.length} 条</span>
                    </div>
                    <div className="space-y-3">
                      {c.suggestions.map((s, si) => {
                        const accepted = s.decision === 'accepted';
                        const ignored = s.decision === 'ignored';
                        return (
                          <div key={s.id} className={`rounded-lg border p-3 ${accepted ? 'border-green-300 bg-green-50/40' : ignored ? 'border-gray-200 bg-gray-50 opacity-60' : 'border-gray-200'}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${typeColor[s.type || ''] || 'bg-gray-100 text-gray-600'}`}>{s.type || '建议'}</span>
                              <span className="text-xs text-gray-500">{s.reason}</span>
                              <div className="ml-auto flex gap-1">
                                <button onClick={() => setDecision(realCi, si, accepted ? 'pending' : 'accepted')}
                                  className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${accepted ? 'bg-green-600 text-white' : 'border border-green-300 text-green-700 hover:bg-green-50'}`}>
                                  <Check className="h-3 w-3" /> 采纳
                                </button>
                                <button onClick={() => setDecision(realCi, si, ignored ? 'pending' : 'ignored')}
                                  className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${ignored ? 'bg-gray-500 text-white' : 'border border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                                  <X className="h-3 w-3" /> 忽略
                                </button>
                              </div>
                            </div>
                            <div className="text-xs text-gray-400 mb-1 line-through break-words">{s.original}</div>
                            <textarea
                              value={s.text}
                              disabled={jobState !== 'review'}
                              onChange={e => setText(realCi, si, e.target.value)}
                              rows={Math.min(4, Math.ceil((s.text.length || 1) / 40))}
                              className="w-full text-sm border border-gray-200 rounded p-2 focus:border-blue-400 focus:ring-1 focus:ring-blue-100 disabled:bg-gray-50"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 flex items-center gap-3 flex-wrap">
              {jobState !== 'done' && (
                <button
                  onClick={handleApply}
                  disabled={jobState === 'applying'}
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-lg font-medium disabled:opacity-40 hover:bg-blue-700 transition"
                >
                  {jobState === 'applying' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                  应用修改 → 校对 + 合并
                </button>
              )}
              {jobState === 'applying' && <span className="text-xs text-gray-400">回填改写、校对、合并中…</span>}
            </div>

            {errorMsg && jobState === 'error' && (
              <div className="flex items-center gap-2 text-red-600 text-sm mt-3"><AlertCircle className="h-4 w-4" /> {errorMsg}</div>
            )}

            {jobState === 'done' && (
              <div className="mt-4 p-4 rounded-lg bg-green-50 border border-green-200">
                <p className="text-green-800 text-sm flex items-center gap-2 font-medium">
                  <CheckCircle className="h-4 w-4" /> 已采纳 {acceptedSug} 处修改 · 校对合并完成 · 任务 {jobId.slice(0, 8)}
                </p>
                <a href={downloadUrl} className="mt-3 inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700">
                  <FileText className="h-4 w-4" /> 下载合并 docx
                </a>
              </div>
            )}
          </section>
        )}

        {/* 面板：拆章结果 */}
        {activeTab === 'chapter' && (
          <section className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">拆章结果</h2>
            {chapters.length === 0 ? (
              <p className="text-sm text-gray-500">上传原稿并开始后，按标题样式自动拆分为章节，这里会列出每一节。</p>
            ) : (
              <>
                <ol className="divide-y divide-gray-100">
                  {chapters.map(c => (
                    <li key={c.n} className="flex items-center gap-3 py-2 text-sm">
                      <span className="w-16 shrink-0 text-gray-400">{c.n === 0 ? '前置' : `第 ${c.n} 章`}</span>
                      <span className="flex-1 text-gray-800 truncate">{c.title}</span>
                      {c.paragraphs != null && <span className="text-xs text-gray-400 shrink-0">{c.paragraphs} 段</span>}
                    </li>
                  ))}
                </ol>
                <p className="text-xs text-gray-400 mt-3">共 {chapters.length} 节（含前置内容/扉页目录）。</p>
              </>
            )}
          </section>
        )}

        {/* 面板：文字校对 */}
        {activeTab === 'proofread' && (
          <section className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">文字校对</h2>
            {jobState === 'done' ? (
              <>
                <p className="text-sm text-green-700 mb-3 flex items-center gap-1">
                  <CheckCircle className="h-4 w-4" /> 已在采纳修改的基础上完成机械校对（错别字 / 标点 / 格式）。
                </p>
                <ol className="divide-y divide-gray-100">
                  {chapters.map(c => (
                    <li key={c.n} className="flex items-center gap-3 py-2 text-sm">
                      <span className="w-16 shrink-0 text-gray-400">{c.n === 0 ? '前置' : `第 ${c.n} 章`}</span>
                      <span className="flex-1 text-gray-800 truncate">{c.title}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 shrink-0">已校对</span>
                    </li>
                  ))}
                </ol>
              </>
            ) : (
              <p className="text-sm text-gray-500">在「编辑反馈」采纳修改并点击“应用修改”后，会自动对全部章节做机械校对。</p>
            )}
          </section>
        )}

        {/* 面板：合并 */}
        {activeTab === 'combine' && (
          <section className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">合并</h2>
            {jobState === 'done' && downloadUrl ? (
              <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                <p className="text-green-800 text-sm flex items-center gap-2 font-medium">
                  <CheckCircle className="h-4 w-4" /> 已合并为整本 combined.docx（{chapters.length} 节）
                </p>
                <a href={downloadUrl} className="mt-3 inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700">
                  <FileText className="h-4 w-4" /> 下载合并 docx
                </a>
              </div>
            ) : (
              <p className="text-sm text-gray-500">校对完成后自动把各章合并成整本，可在此下载 docx。</p>
            )}
          </section>
        )}

        {/* 面板：尚未接入的环节 */}
        {['translate', 'translate-check', 'pagestyle', 'finalcheck', 'copyright', 'final', 'cover'].includes(activeTab) && (
          <section className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">{activeStage?.name || activeTab}</h2>
            <p className="text-sm text-gray-600">{activeStage?.desc}</p>
            <div className="mt-3 inline-flex items-center gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded px-3 py-2">
              <AlertCircle className="h-4 w-4" /> 该环节尚未接入，敬请期待。
            </div>
          </section>
        )}

        <p className="text-xs text-gray-400 mt-2">
          点击上方任一环节即可查看该环节的结果。人工分配 ISBN 后生成版权页与封底条码 → 上传 KDP。
        </p>
        </>
        )}
      </div>
    </div>
  );
};

export default Studio;
